import { useEffect, useState } from 'react'
import CalendarHeatmap from '@/components/charts/CalendarHeatmap'
import DayDetailPanel from '@/components/DayDetailPanel'
import { recoveryHeatmap, type DailyPoint } from '@/db/queries'
import clsx from 'clsx'

type Metric = 'recovery' | 'strain' | 'sleep_performance'
type Range = 90 | 180 | 365 | 730 | 1825

const METRICS: { id: Metric; label: string }[] = [
  { id: 'recovery', label: 'Recovery' },
  { id: 'strain', label: 'Strain' },
  { id: 'sleep_performance', label: 'Sleep' },
]
const RANGES: { id: Range; label: string }[] = [
  { id: 90, label: '90D' },
  { id: 180, label: '6M' },
  { id: 365, label: '1Y' },
  { id: 730, label: '2Y' },
  { id: 1825, label: '5Y' },
]

export default function Calendar() {
  const [metric, setMetric] = useState<Metric>('recovery')
  const [range, setRange] = useState<Range>(90)
  const [data, setData] = useState<DailyPoint[]>([])
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  useEffect(() => {
    recoveryHeatmap(range).then(setData)
  }, [range])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Calendar</h1>
        <div className="flex gap-3">
          <Toggle
            options={METRICS.map((m) => ({ id: m.id, label: m.label }))}
            value={metric}
            onChange={(v) => setMetric(v as Metric)}
          />
          <Toggle
            options={RANGES.map((r) => ({ id: String(r.id), label: r.label }))}
            value={String(range)}
            onChange={(v) => setRange(Number(v) as Range)}
          />
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <CalendarHeatmap
          data={data}
          metric={metric}
          daysBack={range}
          selectedDay={selectedDay}
          onSelectDay={(d) => setSelectedDay(d)}
        />
        <Legend metric={metric} />
      </div>

      <DayDetailPanel day={selectedDay} onClose={() => setSelectedDay(null)} />
    </div>
  )
}

function Toggle({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex gap-1">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={clsx(
            'px-3 py-1 text-sm rounded',
            value === o.id
              ? 'bg-brand/25 text-brand-fg font-medium'
              : 'text-stone-600 hover:bg-stone-100',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function Legend({ metric }: { metric: Metric }) {
  if (metric === 'strain') {
    return (
      <div className="flex items-center gap-2 mt-4 text-xs text-stone-500">
        <span>Less</span>
        {['bg-sky-100', 'bg-sky-200', 'bg-sky-400', 'bg-sky-600', 'bg-sky-800'].map((c) => (
          <span key={c} className={clsx('rounded-sm', c)} style={{ width: 12, height: 12 }} />
        ))}
        <span>More</span>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 mt-4 text-xs text-stone-500">
      <span>Low</span>
      {['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-400', 'bg-green-500'].map((c) => (
        <span key={c} className={clsx('rounded-sm', c)} style={{ width: 12, height: 12 }} />
      ))}
      <span>High</span>
    </div>
  )
}
